import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../utils/AuthContext';
import { VivahLogoIcon } from '../../components/VivahLogo';

export default function Login() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('username'); // 'username' | 'phone'
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr('');
    try {
      await login(form.username.trim(), form.password);
      navigate('/consultation/profile');
    } catch (d) {
      setErr(d?.error || d?.detail || 'Invalid credentials. Please verify your username/phone and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 180px)', padding: '2.5rem 1rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: '460px', padding: '2.5rem 2.2rem', border: '1.5px solid #D4AF37', boxShadow: '0 12px 40px rgba(158,27,50,0.08)' }}>
        
        {/* If user is already logged in */}
        {user ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🙏</div>
            <span className="badge-verified" style={{ marginBottom: '0.8rem', display: 'inline-block' }}>
              ✓ Authenticated Active Session
            </span>
            <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.85rem', color: '#9E1B32', marginBottom: '0.4rem' }}>
              Welcome, {user.full_name || user.username}!
            </h2>
            <p style={{ fontSize: '0.88rem', color: '#6E615E', marginBottom: '1.8rem' }}>
              You are currently logged in to your marriage profile.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <button className="btn btn-primary btn-full" onClick={() => navigate('/consultation/profile')}>
                👤 View My Marriage Profile →
              </button>
              <button className="btn btn-secondary btn-full" onClick={() => navigate('/consultation/payment')}>
                📲 Complete QR Payment →
              </button>
              <button className="btn btn-tertiary btn-full" style={{ color: '#9E1B32', borderColor: '#9E1B32', marginTop: '0.4rem' }} onClick={logout}>
                🚪 Log Out & Switch Account
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header branding */}
            <div style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.8rem' }}>
                <VivahLogoIcon size={46} />
              </div>
              <span className="badge-light" style={{ marginBottom: '0.5rem' }}>✦ Confidential Marriage Portal ✦</span>
              <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.9rem', color: '#9E1B32', margin: '0.2rem 0' }}>
                Welcome Back
              </h2>
              <p style={{ fontSize: '0.86rem', color: '#6E615E' }}>
                Managed by Consultant <strong>Laxmikant Timilsina (9857074103)</strong>
              </p>
            </div>

            {/* Login method selector tabs */}
            <div style={{ display: 'flex', background: '#FAF7F2', borderRadius: '12px', padding: '3px', marginBottom: '1.5rem', border: '1px solid #E8DFD1' }}>
              <button
                type="button"
                onClick={() => setLoginMethod('username')}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.5rem',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  borderRadius: '10px',
                  border: 'none',
                  background: loginMethod === 'username' ? '#FFFFFF' : 'transparent',
                  color: loginMethod === 'username' ? '#9E1B32' : '#6E615E',
                  boxShadow: loginMethod === 'username' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                👤 Username
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('phone')}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.5rem',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  borderRadius: '10px',
                  border: 'none',
                  background: loginMethod === 'phone' ? '#FFFFFF' : 'transparent',
                  color: loginMethod === 'phone' ? '#9E1B32' : '#6E615E',
                  boxShadow: loginMethod === 'phone' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📱 Phone Number
              </button>
            </div>

            {/* Error banner */}
            {err && (
              <div style={{ background: '#FEECEC', color: '#9E1B32', border: '1px solid rgba(158,27,50,0.3)', borderRadius: '12px', padding: '0.85rem 1rem', marginBottom: '1.2rem', fontSize: '0.86rem', fontWeight: 600 }}>
                ⚠️ {err}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.2rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', marginBottom: '0.4rem', textTransform: 'uppercase' }}>
                  {loginMethod === 'phone' ? 'Phone Number *' : 'Username or Phone *'}
                </label>
                <input
                  type={loginMethod === 'phone' ? 'tel' : 'text'}
                  className="form-input"
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  placeholder={loginMethod === 'phone' ? 'e.g. 9857074103' : 'e.g. ramesh_sharma'}
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1C1014', textTransform: 'uppercase' }}>
                    Password *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ background: 'none', border: 'none', color: '#8C6A23', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                  >
                    {showPassword ? '🙈 Hide' : '👁️ Show'}
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <button
                className="btn btn-primary btn-full"
                type="submit"
                disabled={loading}
                style={{ padding: '0.9rem', fontSize: '0.95rem', opacity: loading ? 0.7 : 1 }}
              >
                {loading ? '⏳ Signing in…' : 'Sign In to Profile →'}
              </button>
            </form>

            <div style={{ marginTop: '1.5rem', paddingTop: '1.2rem', borderTop: '1px solid #E8DFD1', textAlign: 'center' }}>
              <p style={{ fontSize: '0.86rem', color: '#6E615E', marginBottom: '0.6rem' }}>
                Don't have a profile yet?{' '}
                <Link to="/consultation/register" style={{ color: '#9E1B32', fontWeight: 800, textDecoration: 'underline' }}>
                  Register Marriage Profile
                </Link>
              </p>
              
              <a
                href="https://wa.me/9779857074103?text=Namaste%20Laxmikant%20ji,%20I%20need%20help%20logging%20in%20to%20my%20Vivah%20Nepal%20profile."
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.78rem', color: '#8C6A23', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
              >
                💬 Need help signing in? Contact Laxmikant Timilsina on WhatsApp
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}